import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shadcnComponents/ui/sheet";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/shadcnComponents/ui/carousel";

import { PiDownloadSimpleBold, PiShareNetworkBold } from "react-icons/pi";
import React, { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import {
  Wrapped2024TotalWords,
  Wrapped2024FavorableMonth,
  Wrapped2024TotalImpressions,
  Wrapped2024FavorableDayOfWeek,
  Wrapped2024LongestStreak,
  Wrapped2024TotalLikes,
  Wrapped2024TotalComments,
  Wrapped2024MostEngagedPostType,
  Wrapped2024MostFavorableDay,
  Wrapped2024MonthlyTrend,
} from "./YearWrap2024Cards";
import { Button } from "@/shadcnComponents/ui/button";

import { useGeneralSettings } from "@/Providers/General";
import { YearWrap2024MetricsData } from "@/Providers/Influencer";
import { ArrowRight } from "lucide-react";
import mixpanel from "mixpanel-browser";
import domtoimage from "dom-to-image";

export const SheetTriggerForWrap2024 = ({
  title,
  description,
  triggerElement,
  startIndex = 0,
  influencerYearWrap,
  onDownlaodAll,
}: {
  title?: string;
  description?: string;
  triggerElement: React.ReactNode;
  startIndex?: number;
  influencerYearWrap: YearWrap2024MetricsData;
  onDownlaodAll: () => void;
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  const generalState = useGeneralSettings();

  const components = [
    {
      component: (
        <Wrapped2024TotalImpressions
          cardValue={influencerYearWrap?.metrics?.total_impressions}
          width={450}
          height={450}
          containerClassName={`carousel-item_Total_Impressions`}
        />
      ),
      name: "Total_Impressions",
    },
    {
      component: (
        <Wrapped2024LongestStreak
          cardValue={influencerYearWrap?.longest_streak?.duration}
          startDate={influencerYearWrap?.longest_streak?.start_date}
          endDate={influencerYearWrap?.longest_streak?.end_date}
          width={450}
          height={450}
          containerClassName={`carousel-item_Longest_Streak`}
        />
      ),
      name: "Longest_Streak",
    },
    {
      component: (
        <Wrapped2024TotalWords
          cardValue={influencerYearWrap?.metrics?.total_characters}
          width={450}
          height={450}
          containerClassName={`carousel-item_Total_Words`}
        />
      ),
      name: "Total_Words",
    },
    {
      component: (
        <Wrapped2024FavorableMonth
          heading={influencerYearWrap?.text?.month}
          cardValue={influencerYearWrap?.most_favorable?.month?.month_number}
          width={450}
          height={450}
          containerClassName={`carousel-item_Favorable_Month`}
        />
      ),
      name: "Favorable_Month",
    },

    {
      component: (
        <Wrapped2024FavorableDayOfWeek
          cardValue={
            influencerYearWrap?.most_favorable?.day_of_week?.day_number
          }
          width={450}
          height={450}
          containerClassName={`carousel-item_Favorable_Day_Of_Week`}
        />
      ),
      name: "Favorable_Day_Of_Week",
    },

    {
      component: (
        <Wrapped2024TotalLikes
          heading={influencerYearWrap?.text?.likes}
          cardValue={influencerYearWrap?.metrics?.total_likes}
          width={450}
          height={450}
          containerClassName={`carousel-item_Total_Likes`}
        />
      ),
      name: "Total_Likes",
    },
    {
      component: (
        <Wrapped2024TotalComments
          heading={influencerYearWrap?.text?.comments}
          cardValue={influencerYearWrap?.metrics?.total_comments}
          width={450}
          height={450}
          containerClassName={`carousel-item_Total_Comments`}
        />
      ),
      name: "Total_Comments",
    },
    {
      component: (
        <Wrapped2024MostEngagedPostType
          cardValue={influencerYearWrap?.most_engaged_posts?.[0]?.post_type}
          width={450}
          height={450}
          containerClassName={`carousel-item_Most_Engaged_Post_Type`}
        />
      ),
      name: "Most_Engaged_Post_Type",
    },
    {
      component: (
        <Wrapped2024MostFavorableDay
          impressions={influencerYearWrap?.most_favorable?.day?.impressions}
          cardValue={influencerYearWrap?.most_favorable?.day?.date}
          width={450}
          height={450}
          containerClassName={`carousel-item_Most_Favorable_Day`}
        />
      ),
      name: "Most_Favorable_Day",
    },
    {
      component: (
        <Wrapped2024MonthlyTrend
          monthlyTrend={influencerYearWrap?.monthlyTrend}
          heading={influencerYearWrap?.text?.monthlyTrendHeader}
          subheading={influencerYearWrap?.text?.monthlyTrendSubHeader}
          footerText={influencerYearWrap?.text?.monthlyTrendFooter}
          width={450}
          height={450}
          containerClassName={`carousel-item_Monthly_Trend`}
        />
      ),
      name: "Monthly_Trend",
    },
  ];

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  useEffect(() => {
    if (api) {
      api.scrollTo(startIndex);
    }
  }, [api, startIndex]);

  const downloadCurrentSlide = async () => {
    generalState?.setHeartLoading(true);
    const currentSlideElement = document.querySelector(
      `.carousel-item_${components?.[current - 1]?.name}`
    ) as HTMLElement;

    if (!currentSlideElement) return;

    try {
      const dataUrl = await domtoimage.toPng(currentSlideElement, {
        quality: 1,
        bgcolor: "transparent",
        style: {
          transform: "none",
          animation: "none",
          opacity: "1",
        },
      });

      const link = document.createElement("a");
      link.download = `exclusive-linkedin-unwrapped-by-anchors_${
        components[current - 1].name
      }.png`;
      link.href = dataUrl;
      link.click();
      generalState?.setHeartLoading(false);
    } catch (error) {
      generalState?.setHeartLoading(false);
      console.error("Error exporting image:", error);
    }
  };

  const shareToLinkedIn = async () => {
    try {
      const shareText = `
${influencerYearWrap?.metrics?.total_characters || 0} characters
${influencerYearWrap?.metrics?.total_likes || 0} likes
${influencerYearWrap?.metrics?.total_comments || 0} comments

The fact that my content impacted ${
        influencerYearWrap?.metrics?.total_impressions || 0
      } people around the globe is humbling!

Reflecting on my 2024 LinkedIn journey, ${
        influencerYearWrap?.most_favorable?.month?.month_number || "N/A"
      } turned out to be my most favorable month.

🔍 Here are my biggest takeaways:
- Consistency is key; my best days were every ${
        influencerYearWrap?.most_favorable?.day_of_week?.day_number || "N/A"
      }
- Connections matter; I grew alongside a vibrant community of creators
- Authenticity wins; diving deep into the topics that resonated most with you all.

2024 has been an incredible year of growth and connection!

If someone told me back in January that so much would happen in the span of a year, I wouldn't have believed them. 

But here we are.

Excited for what the next chapter holds as I continue to evolve!

#LinkedInUnwrapped #exclusive #anchors
      `;

      // Encode the text for URL compatibility
      const encodedText = encodeURIComponent(shareText.trim());

      // LinkedIn share URL
      const linkedInShareURL = `https://www.linkedin.com/feed/?linkOrigin=LI_BADGE&shareActive=true&text=${encodedText}`;

      // Open LinkedIn share window
      window.open(linkedInShareURL, "_blank");
    } catch (error) {
      console.error("Error sharing to LinkedIn:", error);
    }
  };

  return (
    <>
      <Sheet>
        <SheetTrigger asChild>
          <div className="cursor-pointer transition-transform hover:scale-105">
            {triggerElement}
          </div>
        </SheetTrigger>
        <SheetContent
          style={{
            scrollbarWidth: "none",
          }}
          className="inline-flex w-[500px] overflow-y-auto sm:max-w-lg flex-col items-center gap-6 shrink-0 bg-[color:var(--Natural-900,#212121)] p-6 border-none "
        >
          {(title || description) && (
            <SheetHeader>
              {title && <SheetTitle>Edit profile</SheetTitle>}
              {description && (
                <SheetDescription>
                  Make changes to your profile here. Click save when you're
                  done.
                </SheetDescription>
              )}
            </SheetHeader>
          )}

          <Carousel
            setApi={setApi}
            className="w-full max-w-xs flex flex-col items-center"
          >
            <CarouselContent className="w-[500px] h-[550px]">
              {components.map((item, index) => (
                <CarouselItem key={`Carousel_${index}`} className={``}>
                  <div className="w-full flex p-1 items-center justify-center">
                    {item.component}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center justify-center w-full space-x-4">
              <CarouselPrevious
                icon={<FaChevronLeft size={24} />}
                className="top-full bg-transparent border-none hover:bg-transparent text-[#E0E0E0] hover:text-[#fff] "
              />
              <div className="flex space-x-2">
                {Array.from({ length: count }).map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full ${
                      index === current - 1 ? "bg-[#D60000]" : "bg-[#424242]"
                    }`}
                  />
                ))}
              </div>
              <CarouselNext
                icon={<FaChevronRight size={24} />}
                className="top-full bg-transparent border-none hover:bg-transparent text-[#E0E0E0] hover:text-[#fff] "
              />
            </div>
          </Carousel>

          <SheetFooter className="w-full mt-3 ">
            <div className="w-full flex flex-col items-center gap-6">
              <div className=" w-full flex items-center gap-3 self-stretch">
                <Button
                  className="flex w-full justify-center items-center gap-1.5 flex-[1_0_0] bg-transparent border border-b border-[#424242] hover:bg-white hover:text-black px-[18px] py-3 rounded-[5px]"
                  type="submit"
                  onClick={() => {
                    mixpanel.track("Year Wrap 2024: Download Png: Clicked");
                    downloadCurrentSlide();
                  }}
                >
                  Download PNG
                  <PiDownloadSimpleBold size={16} />
                </Button>
                <Button
                  className="flex w-full bg-transparent justify-center items-center gap-1.5 flex-[1_0_0] bg-[#0077B5] hover:bg-white hover:text-black border border-[color:var(--Natural-800,#424242)] px-[18px] py-3 rounded-[5px] border-solid"
                  type="submit"
                  onClick={() => {
                    mixpanel.track(
                      "Year Wrap 2024: Share on Linkedin: Clicked"
                    );
                    shareToLinkedIn();
                  }}
                >
                  Share on Linkedin
                  <PiShareNetworkBold size={16} />
                </Button>
              </div>
              <span
                onClick={() => {
                  mixpanel.track(
                    "Year Wrap 2024: Download PDF for Carousal: Clicked"
                  );
                  onDownlaodAll();
                }}
                className="cursor-pointer flex h-6 justify-center items-center gap-2.5 shrink-0 px-4 py-0 text-[#DC2627] text-xs not-italic font-bold leading-6 underline decoration-solid decoration-auto underline-offset-auto"
              >
                Download PDF for Carousal <ArrowRight />{" "}
              </span>
              <span className="text-white text-xs italic">
                Note: When sharing on Linkedin, attach the downloaded PDF
                directly.
              </span>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};
